import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@hqo/ui-atoms';
import { Controller, Control, FieldErrors } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useMemo } from 'react';
import { CreateVisitFormV2Values } from '../../../types/form-data.types';
import { calculateMaxDuration } from '../when-step.utils';
import { useValidationMessage } from '../../../hooks/use-validation-message.hook';

interface DurationInputFieldProps {
  control: Control<CreateVisitFormV2Values>;
  errors: FieldErrors<CreateVisitFormV2Values>;
  startTime?: string;
  endTimeDisplay?: string;
  onDurationChange?: (value: string, onChange: (num: number) => void) => void;
}

export const DurationInputField = ({
  control,
  errors,
  startTime,
  endTimeDisplay,
  onDurationChange,
}: DurationInputFieldProps): JSX.Element => {
  const { formatMessage } = useIntl();
  const formatValidationMessage = useValidationMessage();
  const error = errors.duration;

  // Calculate available duration options based on start time
  const durationOptions = useMemo(() => {
    const maxDuration = startTime ? calculateMaxDuration(startTime) : 8;
    // Generate options from 1 to maxDuration (inclusive)
    return Array.from({ length: maxDuration }, (_, i) => i + 1);
  }, [startTime]);

  return (
    <div className="flex flex-col">
      <Controller
        name="duration"
        control={control}
        // eslint-disable-next-line react/jsx-no-bind
        render={({ field }) => (
          <>
            <Select
              value={String(field.value || 1)}
              // eslint-disable-next-line react/jsx-no-bind
              onValueChange={(value) => {
                if (onDurationChange) {
                  onDurationChange(value, field.onChange);
                } else {
                  field.onChange(Number(value));
                }
              }}
            >
              <SelectTrigger id="duration" className={`w-full ${error ? 'border-destructive' : ''}`}>
                <SelectValue
                  placeholder={formatMessage({
                    id: 'createVisitFormV2.whenStep.selectDuration',
                    defaultMessage: 'Select duration',
                  })}
                />
              </SelectTrigger>
              <SelectContent>
                {durationOptions.map((hours) => (
                  <SelectItem key={hours} value={String(hours)}>
                    {hours}{' '}
                    {hours === 1
                      ? formatMessage({
                          id: 'createVisitFormV2.whenStep.hour',
                          defaultMessage: 'hour',
                        })
                      : formatMessage({
                          id: 'createVisitFormV2.whenStep.hours',
                          defaultMessage: 'hours',
                        })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {/* Error message area - fixed height, but with calculated end time */}
            <div className="mt-1 h-5 flex items-center">
              {error ? (
                <p className="text-sm text-destructive">{formatValidationMessage(error.message as string)}</p>
              ) : (
                endTimeDisplay && (
                  <p className="text-xs text-muted-foreground">
                    <FormattedMessage
                      id="createVisitFormV2.whenStep.endTimeLabel"
                      defaultMessage="End time: {endTimeDisplay}"
                      values={{ endTimeDisplay }}
                    />
                  </p>
                )
              )}
            </div>
          </>
        )}
      />
    </div>
  );
};
